import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { supabase } from "./lib/supabaseClient";

import Landing from "./pages/Landing";
import AuthResetPassword from "./pages/AuthResetPassword";
import ScoutsLanding from "./pages/ScoutsLanding";
import OperatorsLanding from "./pages/OperatorsLanding";
import BusinessesRequest from "./pages/BusinessesRequest";
import CreatorsLanding from "./pages/CreatorsLanding";
import Marketplace from "./pages/Marketplace";
import OpportunityDetail from "./pages/OpportunityDetail";
import OperatorDashboard from "./pages/OperatorDashboard";
import OperatorLogin from "./pages/OperatorLogin";
import OperatorPortal from "./pages/OperatorPortal";
import BusinessVerification from "./pages/BusinessVerification";
import CreatorDashboard from "./pages/CreatorDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLogin from "./pages/AdminLogin";
import AdminOutreachSetupSecrets from "./pages/AdminOutreachSetupSecrets";
import ScoutLogin from "./pages/ScoutLogin";
import ScoutDashboard from "./pages/ScoutDashboard";
import ScoutFindLocation from "./pages/ScoutFindLocation";
import ManagerView from "./pages/ManagerView";
import AgreementView from "./pages/AgreementView";
import LaunchReadiness from "./pages/LaunchReadiness";
import Scan from "./pages/Scan";
import Sell from "./pages/Sell";
import ReferralRedirect from "./pages/ReferralRedirect";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Faq from "./pages/Faq";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";

import ScoutShell from "./pages/scout/ScoutShell";
import ScoutHome from "./pages/scout/ScoutHome";
import SubmitSpot from "./pages/scout/SubmitSpot";
import MySpots from "./pages/scout/MySpots";
import Earnings from "./pages/scout/Earnings";
import Referrals from "./pages/scout/Referrals";
import Campaigns from "./pages/scout/Campaigns";
import ScoutProfile from "./pages/scout/ScoutProfile";

import PrototypeNav from "./components/PrototypeNav";
import "./App.css";

// Feature flags (never hard-code defaults for env-driven behavior)
const PROTOTYPE_ENABLED = String(process.env.REACT_APP_ENABLE_PROTOTYPE || "").toLowerCase() === "true";

function App() {
  // Global PASSWORD_RECOVERY interceptor.
  // MUST run before any route guard so recovery sessions cannot silently
  // land on /admin, /scouts/dashboard, or /operator/dashboard.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        try { sessionStorage.setItem("spotbounty_password_recovery_active", "true"); } catch { /* ignore */ }
        // Hard redirect wins the race against React Router navigation in login pages.
        if (window.location.pathname !== "/auth/reset-password") {
          window.location.replace("/auth/reset-password");
        }
      }
    });
    return () => { try { sub?.subscription?.unsubscribe?.(); } catch { /* ignore */ } };
  }, []);

  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-[#080B0D] text-white">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth/reset-password" element={<AuthResetPassword />} />
            <Route path="/scouts" element={<ScoutsLanding />} />
            <Route path="/operators" element={<OperatorsLanding />} />
            <Route path="/businesses" element={<BusinessesRequest />} />
            <Route path="/creators" element={<CreatorsLanding />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/contact" element={<Contact />} />

            {/* Owner-only admin */}
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/launch-readiness" element={<LaunchReadiness />} />
            <Route path="/admin/outreach/setup-secrets" element={<AdminOutreachSetupSecrets />} />
            <Route path="/scouts/login" element={<ScoutLogin />} />
            <Route path="/scouts/dashboard" element={<ScoutDashboard />} />
            <Route path="/scouts/find-location/:id" element={<ScoutFindLocation />} />
            <Route path="/l/:token" element={<ManagerView />} />
            <Route path="/a/:token" element={<AgreementView />} />
            <Route path="/scan" element={<Scan />} />
            <Route path="/sell" element={<Sell />} />

            {/* Public referral redirects */}
            <Route path="/r/:code" element={<ReferralRedirect target="/scouts" />} />
            <Route path="/c/:code" element={<ReferralRedirect target="/creators" />} />

            {/* Prototype-only mock screens — gated behind REACT_APP_ENABLE_PROTOTYPE */}
            <Route path="/operator/login" element={<OperatorLogin />} />
            <Route path="/operator/*" element={<OperatorPortal />} />

            {PROTOTYPE_ENABLED && (
              <>
                <Route path="/app/scout" element={<ScoutShell />}>
                  <Route index element={<ScoutHome />} />
                  <Route path="submit" element={<SubmitSpot />} />
                  <Route path="spots" element={<MySpots />} />
                  <Route path="earnings" element={<Earnings />} />
                  <Route path="referrals" element={<Referrals />} />
                  <Route path="campaigns" element={<Campaigns />} />
                  <Route path="profile" element={<ScoutProfile />} />
                </Route>
                <Route path="/marketplace" element={<Marketplace />} />
                <Route path="/marketplace/:id" element={<OpportunityDetail />} />
                <Route path="/operator-preview" element={<OperatorDashboard />} />
                <Route path="/verify/:token" element={<BusinessVerification />} />
                <Route path="/creator" element={<CreatorDashboard />} />
              </>
            )}

            <Route path="*" element={<NotFound />} />
          </Routes>
          {PROTOTYPE_ENABLED && <PrototypeNav />}
          <Toaster
            theme="dark"
            position="top-center"
            toastOptions={{
              style: {
                background: "#15191D",
                border: "1px solid #2A2F34",
                color: "#fff",
              },
            }}
          />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
